'use client';

import { useTranslations } from 'next-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, alpha } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ConversationPreview, BotInspirationFeed } from '../shared';
import { FeatureHighlights } from '../FeatureHighlights';
import { HelpChat } from '../HelpChat';
import { trackCTAClick } from '../../../lib/analytics';
import { useInspirationFeedEnabled } from '../../../lib/posthogABTesting';
import type { BotIdea } from '../content/botIdeas';
import type { ReactNode } from 'react';

/**
 * Variant A: Conversation First
 *
 * Layout: Chat preview prominent (60-70% viewport), CTA on right
 * Target: Skeptical users who need to see proof of bot quality
 *
 * Desktop: Two-column with conversation preview dominant
 * Mobile: Single column with conversation above form
 *
 * Story 6.1: Marketing Landing Page with A/B Variants
 */

interface VariantAProps {
  /** The content input form as children */
  children: ReactNode;
  /** Whether value props are collapsed (user started creating) */
  collapsed?: boolean;
  /** Content to show on the left side when collapsed */
  leftContent?: ReactNode;
  /** Callback when a bot idea chip is clicked (Story 6.13) */
  onIdeaClick?: (idea: BotIdea) => void;
}

export function VariantA({
  children,
  collapsed = false,
  leftContent,
  onIdeaClick,
}: VariantAProps) {
  const t = useTranslations('creator.landing');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enabled: showInspirationFeed } = useInspirationFeedEnabled();

  // Mobile layout: Single column with conversation preview above
  if (isMobile) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
        }}
      >
        {/* Collapsible conversation preview */}
        <Collapse in={!collapsed} timeout={300}>
          <Box sx={{ mb: 2, bgcolor: alpha(theme.palette.primary.main, 0.08), borderRadius: 2, p: 2 }}>
            {/* Header */}
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{ textAlign: 'center', mb: 2 }}
            >
              {t('welcome.title')}
            </Typography>

            {/* Conversation preview */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                p: 2,
                maxHeight: 250,
                overflow: 'hidden',
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1.5 }}
              >
                {t('variants.a.seeItInAction')}
              </Typography>
              <ConversationPreview
                maxMessages={4}
                showSourceHints={false}
                maxHeight={180}
              />
            </Box>
          </Box>
        </Collapse>

        {/* Feature highlights — mobile (Story 6.16) */}
        <Collapse in={!collapsed} timeout={300}>
          <FeatureHighlights variant="icons-only" />
        </Collapse>

        {/* Inspiration feed — mobile (Story 6.13) */}
        <Collapse in={!collapsed && showInspirationFeed} timeout={300} unmountOnExit>
          <BotInspirationFeed variant="horizontal" onIdeaClick={onIdeaClick} pageVariant="A" feedPosition="below-conversation-preview" />
        </Collapse>

        {/* Creation form with visual grouping */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography
            variant="h5"
            fontWeight={600}
            color="primary.main"
            sx={{ mb: 0.5 }}
          >
            {t('contentSection.title')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            {t('contentSection.subtitle')}
          </Typography>
          {children}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: 'block', textAlign: 'center' }}
          >
            {t('contentSection.helperText')}
          </Typography>
        </Box>

        {/* Note: leftContent is NOT shown on mobile - UnifiedContentPanel handles the list */}

        {/* Help chat at bottom (only when not collapsed) */}
        <Collapse in={!collapsed} timeout={300}>
          <HelpChat />
        </Collapse>
      </Box>
    );
  }

  // Desktop layout: Conversation preview on left (60-70%), form on right
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 4,
        width: '100%',
        minHeight: '70vh',
        alignItems: 'flex-start',
      }}
    >
      {/* Left column: Conversation preview (dominant) OR custom content when collapsed */}
      {collapsed && leftContent ? (
        <Box
          sx={{
            flex: '0 0 400px',
            maxWidth: 400,
            position: 'sticky',
            top: 24,
            maxHeight: 'calc(100vh - 48px)',
            overflowY: 'auto',
          }}
        >
          {leftContent}
        </Box>
      ) : (
        <Box
          sx={{
            flex: collapsed ? 0 : '1 1 55%',
            minWidth: collapsed ? 0 : 340,
            maxWidth: collapsed ? 0 : 600,
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            visibility: collapsed ? 'hidden' : 'visible',
            opacity: collapsed ? 0 : 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              pt: 2,
              position: 'sticky',
              top: 24,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              borderRadius: 3,
              p: 3,
              maxWidth: 600,
              boxSizing: 'border-box',
            }}
          >
            {/* Header */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h4"
                fontWeight={700}
                gutterBottom
                sx={{ fontSize: { md: '1.75rem', lg: '2rem' } }}
              >
                {t('welcome.title')}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 450, mx: 'auto' }}
              >
                {t('variants.a.subtitle')}
              </Typography>
            </Box>

            {/* Conversation preview - prominent */}
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                p: 3,
                boxShadow: 1,
              }}
            >
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', mb: 2, letterSpacing: 1 }}
              >
                {t('variants.a.seeItInAction')}
              </Typography>
              <ConversationPreview
                maxMessages={8}
                showSourceHints={true}
                maxHeight={400}
              />
            </Box>

            {/* Inspiration feed (Story 6.13) */}
            <Collapse in={!collapsed && showInspirationFeed} timeout={300} unmountOnExit>
              <BotInspirationFeed variant="horizontal" onIdeaClick={onIdeaClick} pageVariant="A" feedPosition="below-conversation-preview" />
            </Collapse>

            {/* Feature highlights — desktop (Story 6.16) */}
            <FeatureHighlights variant="icons-only" />

            {/* Secondary CTA */}
            <Button
              variant="outlined"
              size="large"
              endIcon={<ArrowForwardIcon sx={{ transform: theme.direction === 'rtl' ? 'scaleX(-1)' : 'none' }} />}
              onClick={() => {
                // Track CTA click for analytics (AC6)
                trackCTAClick('create_yours', 'variant_a_conversation');
                // Scroll to form
                const form = document.getElementById('content-input');
                form?.scrollIntoView({ behavior: 'smooth' });
              }}
              sx={{
                alignSelf: 'center',
                px: 4,
              }}
            >
              {t('variants.a.createYours')}
            </Button>

            {/* Help chat */}
            <HelpChat />
          </Box>
        </Box>
      )}

      {/* Right column: Creation form with visual grouping */}
      <Box
        id="content-input"
        sx={{
          flex: '1 1 40%',
          minWidth: 0,
          maxWidth: collapsed && leftContent ? 600 : collapsed ? 700 : 500,
          mx: collapsed && !leftContent ? 'auto' : 0,
          transition: 'all 0.3s ease',
          bgcolor: 'background.paper',
          borderRadius: 3,
          p: { xs: 2, md: 3 },
          boxShadow: 1,
        }}
      >
        <Typography
          variant="h4"
          fontWeight={600}
          color="primary.main"
          sx={{ mb: 1 }}
        >
          {t('contentSection.title')}
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          {t('contentSection.subtitle')}
        </Typography>
        {children}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 3, textAlign: 'center' }}
        >
          {t('contentSection.helperText')}
        </Typography>
      </Box>
    </Box>
  );
}
